import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { SelectOption } from '@vchasno/ui-kit';

import { ActionExecutorType, DocumentActionType, TaskStatus } from 'api/api';
import { PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { paginationApi } from 'api/pagination.api';

import { TaskFilterDateVariant, buildStartDateByVariant } from './task.utils';
import { TaskDocumentState } from 'features/taskTemplate/vacation/vacationTaskTemplate.types';
import { toggleDocumentAccessSettings } from 'features/taskTemplate/vacation/vacationTaskTemplate.utils';

interface TaskFilter<T = string> {
  label: string;
  value: T;
}

export interface TaskFilters {
  search?: string;
  company?: TaskFilter[];
  taskTemplate?: SelectOption[];
  dateVariant?: Nullable<TaskFilter<TaskFilterDateVariant>>;
  startDate?: Date;
  endDate?: Date;
  manager?: TaskFilter[];
  status: TaskStatus;
  attentionRequired?: boolean;
  selfTasks?: boolean;
}

export interface EditRouteDocument extends TaskDocumentState {
  name: string;
  routeId: Nullable<string>;
}

interface TaskSliceState {
  filters: TaskFilters;
  paginationPage: number;
  taskId: Nullable<string>;
  activeDetailsTabIndex: number;
  editRouteDocument: EditRouteDocument;
  actionsTykState: Record<string, string>;
}

const initialState: TaskSliceState = {
  taskId: null,
  filters: {
    status: TaskStatus.New,
  },
  paginationPage: 1,
  activeDetailsTabIndex: 0,
  editRouteDocument: {
    name: '',
    routeId: null,
    isActive: true,
    isParallel: false,
    actions: [],
  },
  actionsTykState: {},
};

const persistConfig: PersistConfig<TaskSliceState> = {
  key: 'tasks',
  storage,
  version: CACHE_STORE_VERSION,
  whitelist: ['actionsTykState'],
};

function clearPagination(state: TaskSliceState) {
  state.paginationPage = initialState.paginationPage;
}

export const taskSlice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    setTaskId: (state, action: PayloadAction<string>) => {
      state.taskId = action.payload;
      state.activeDetailsTabIndex = initialState.activeDetailsTabIndex;
    },
    resetTaskId: (state) => {
      state.taskId = null;
    },
    setActiveDetailsTabIndex: (state, action: PayloadAction<number>) => {
      state.activeDetailsTabIndex = action.payload;
    },
    resetActiveDetailsTabIndex: (state) => {
      state.activeDetailsTabIndex = 0;
    },
    setCompaniesFilter: (state, action: PayloadAction<TaskFilter[]>) => {
      clearPagination(state);

      state.filters.company = action.payload;
    },
    clearCompanyFilterValue: (state, action: PayloadAction<string>) => {
      clearPagination(state);

      state.filters.company = state.filters.company?.filter(
        (company) => company.value !== action.payload,
      );
    },
    clearAllCompanyFilters: (state) => {
      clearPagination(state);

      delete state.filters.company;
    },
    setFilterDateVariant: (
      state,
      action: PayloadAction<TaskFilter<TaskFilterDateVariant>>,
    ) => {
      clearPagination(state);

      state.filters.dateVariant = action.payload;
      state.filters.startDate = buildStartDateByVariant(action.payload.value);
      state.filters.endDate = new Date();
    },
    clearDateVariantFilter: (state) => {
      clearPagination(state);

      state.filters.dateVariant = null;
      delete state.filters.startDate;
      delete state.filters.endDate;
    },
    setStartDateFilter: (state, action: PayloadAction<Date>) => {
      clearPagination(state);

      state.filters.dateVariant = null;
      state.filters.startDate = action.payload;
    },
    clearStartDateFilter: (state) => {
      clearPagination(state);

      state.filters.dateVariant = null;
      delete state.filters.startDate;
    },
    setEndDateFilter: (state, action: PayloadAction<Date>) => {
      clearPagination(state);

      state.filters.dateVariant = null;
      state.filters.endDate = action.payload;
    },
    clearEndDateFilter: (state) => {
      clearPagination(state);

      state.filters.dateVariant = null;
      delete state.filters.endDate;
    },
    setManagerFilter: (state, action: PayloadAction<TaskFilter[]>) => {
      clearPagination(state);

      delete state.filters.selfTasks;
      state.filters.manager = action.payload;
    },
    clearManagerFilterValue: (state, action: PayloadAction<string>) => {
      clearPagination(state);

      state.filters.manager = state.filters.manager?.filter(
        (manager) => manager.value !== action.payload,
      );
    },
    clearAllManagerFilters: (state) => {
      clearPagination(state);

      delete state.filters.manager;
    },
    clearAllFilters: (state) => {
      clearPagination(state);

      state.filters = initialState.filters;
    },

    setTaskTemplateTypes: (state, action: PayloadAction<SelectOption[]>) => {
      clearPagination(state);

      state.filters.taskTemplate = action.payload;
    },
    removeTypeFilterItem: (state, action: PayloadAction<string>) => {
      state.filters.taskTemplate = state.filters.taskTemplate?.filter(
        (task) => task.value !== action.payload,
      );
    },

    setFilterStatus: (state, action: PayloadAction<TaskStatus>) => {
      clearPagination(state);

      state.filters.status = action.payload;
    },

    setFilterSearch: (state, action: PayloadAction<string>) => {
      clearPagination(state);

      state.filters.search = action.payload;
    },
    clearFilterSearch: (state) => {
      clearPagination(state);

      delete state.filters.search;
    },
    toggleAttentionRequiredFilter: (state) => {
      clearPagination(state);

      state.filters.attentionRequired = !state.filters.attentionRequired;
    },
    toggleSelfTasksFilter: (state) => {
      clearPagination(state);

      state.filters.selfTasks = !state.filters.selfTasks;
    },
    nextPaginationPage: (state) => {
      state.paginationPage += 1;
    },

    setRouteDocumentToEdit: (
      state,
      action: PayloadAction<EditRouteDocument>,
    ) => {
      Object.assign(state.editRouteDocument, action.payload);
    },
    addRouteDocumentAction: (state, action: PayloadAction<SelectOption>) => {
      state.editRouteDocument.actions.push({
        type: DocumentActionType.Sign,
        executorType: ActionExecutorType.Static,
        executorId: action.payload.value,
        id: Math.random(),
      });
    },
    removeRouteDocumentAction: (state, action: PayloadAction<number>) => {
      state.editRouteDocument.actions.splice(action.payload, 1);
    },
    toggleRouteDocumentActionType: (state, action: PayloadAction<number>) => {
      const currentType = state.editRouteDocument.actions[action.payload].type;

      if (currentType === DocumentActionType.Sign) {
        state.editRouteDocument.actions[action.payload].type =
          DocumentActionType.View;
      } else {
        state.editRouteDocument.actions[action.payload].type =
          DocumentActionType.Sign;
      }
    },
    toggleIsParallelRouteDocument: (state) => {
      state.editRouteDocument.isParallel = !state.editRouteDocument.isParallel;
    },
    toggleRouteDocumentSecuritySetting: (state) => {
      state.editRouteDocument.accessSettings = toggleDocumentAccessSettings(
        state.editRouteDocument.accessSettings,
      );
    },
    reorderRouteDocumentActions: (state, action: PayloadAction<number[]>) => {
      state.editRouteDocument.actions = action.payload.map(
        (index) => state.editRouteDocument.actions[index],
      );
    },
    clearRouteDocument: (state) => {
      state.editRouteDocument = { ...initialState.editRouteDocument };
    },
    setActionsTykAction: (state, action: PayloadAction<string>) => {
      state.actionsTykState[action.payload] = Date.now().toString();
    },
    clearDeprecatedActionsTykState: (state) => {
      state.actionsTykState = Object.entries(state.actionsTykState).reduce(
        (acc, [key, value]) => {
          const date = new Date(Number(value));

          const now = Date.now();
          const diff = now - date.getTime();

          const diffInHours = diff / (1000 * 60 * 60);

          if (diffInHours < 1) {
            acc[key] = value;
          }

          return acc;
        },
        {} as Record<string, string>,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      /** set first task as active after fetching list */
      .addMatcher(
        paginationApi.endpoints.listTasks.matchFulfilled,
        (state, action) => {
          if (state.taskId) return state;

          const firstTask = action.payload.detail.items?.[0];

          if (!firstTask) return state;

          state.taskId = firstTask.id;
        },
      );
  },
  selectors: {
    selectTaskId: (state) => state.taskId,
    selectFilters: (state) => state.filters,
    selectActiveDetailsTabIndex: (state) => state.activeDetailsTabIndex,
    selectSelfTasksFilter: (state) => state.filters.selfTasks,
    selectAttentionRequiredFilter: (state) => state.filters.attentionRequired,
    selectFiltersStatus: (state) => state.filters.status,
    selectPaginationPage: (state) => state.paginationPage,
    selectFiltersSearch: (state) => state.filters.search,
    selectRouteDocumentToEditState: (state) => state.editRouteDocument,
    selectActionsTykState: (state) => state.actionsTykState,
  },
});

export const tasksPersistedReducer = persistReducer(
  persistConfig,
  taskSlice.reducer,
);
